import { Link } from "react-router-dom";
import { ArrowLeft, Download, Pause, Play } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import Cassette from "../components/Cassette";
import useSharedAudio from "../hooks/useSharedAudio";
import { forceDownload } from "../utils/download";

const AUDIO_SIDE_A = `${process.env.PUBLIC_URL}/audio/side-a.mp3`;
const AUDIO_SIDE_B = `${process.env.PUBLIC_URL}/audio/side-b.mp3`;
const AUDIO_SIDE_PL = `${process.env.PUBLIC_URL}/audio/side-pl.mp3`;

const TAPES_HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_clickable-image-nav/artifacts/uqv1i45i_274b732c-0239-4cb3-918c-c24819a89b59.jpeg";

export default function Tapes() {
  const { t } = useLanguage();
  const audioA = useSharedAudio(AUDIO_SIDE_A, true);
  const audioPL = useSharedAudio(AUDIO_SIDE_PL, false);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SiteHeader />

      <main className="flex flex-1 flex-col items-center px-6 py-12 sm:px-10 lg:px-20">
        {/* Hero painting with centered iOS-style play/stop overlay */}
        <div className="relative mb-12 w-full max-w-[1280px]">
          <img
            src={TAPES_HERO_IMAGE}
            alt={t("heroTitle")}
            className="block h-auto w-full select-none"
            draggable={false}
            data-testid="tapes-hero-painting"
          />
          {/* Centered iPhone-style play/pause button */}
          <button
            type="button"
            data-testid="hero-audio-toggle"
            onClick={audioA.toggle}
            disabled={audioA.error}
            aria-label={audioA.isPlaying ? t("pause") : t("play")}
            className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-white backdrop-blur transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 sm:h-20 sm:w-20"
            style={{
              backgroundColor: "rgba(0,0,0,0.55)",
              boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
              border: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            {audioA.isPlaying ? (
              <Pause className="h-7 w-7 fill-current sm:h-9 sm:w-9" />
            ) : (
              <Play className="ml-1 h-7 w-7 fill-current sm:h-9 sm:w-9" />
            )}
          </button>
        </div>

        <div className="mb-8 flex w-full max-w-[1280px] items-center">
          {/* Tapes ist jetzt die Startseite – kein „Zurück zur Startseite" mehr */}
        </div>

        <div className="mb-14 max-w-3xl text-center">
          <p
            className="text-[16px] sm:text-[17px]"
            style={{ color: "#1A1A1A", opacity: 0.85, lineHeight: 1.6 }}
          >
            {t("tapesIntro")}
          </p>
        </div>

        <div
          data-testid="tapes-grid"
          className="grid w-full max-w-[1280px] grid-cols-1 gap-16 md:grid-cols-2 md:gap-12 lg:gap-20"
        >
          <div data-testid="tape-side-a" className="flex flex-col items-center">
            <Cassette
              side="A"
              audioSrc={AUDIO_SIDE_A}
              testIdPrefix="cassette-a"
              downloadName="raus-aus-dem-falschen-leben.mp3"
              externalAudio={audioA}
            />
            <p className="mt-6 text-base" style={{ color: "#1A1A1A", fontWeight: 500, letterSpacing: "0.3px" }}>
              {t("tapes_side_label")} A — {t("sideA")}
            </p>
          </div>

          <div data-testid="tape-side-b" className="flex flex-col items-center">
            <Cassette
              side="B"
              audioSrc={AUDIO_SIDE_B}
              testIdPrefix="cassette-b"
              downloadName="out-of-the-wrong-life.mp3"
            />
            <p className="mt-6 text-base" style={{ color: "#1A1A1A", fontWeight: 500, letterSpacing: "0.3px" }}>
              {t("tapes_side_label")} B — {t("sideB")}
            </p>
          </div>
        </div>

        {/* Polnische Version — schlichter Audio-Player unter den Kassetten */}
        <div
          data-testid="tape-polish-row"
          className="mt-14 flex w-full max-w-[640px] flex-col items-center gap-4 sm:flex-row sm:gap-6"
        >
          <button
            type="button"
            onClick={audioPL.toggle}
            disabled={audioPL.error}
            data-testid="polish-play-button"
            aria-label={audioPL.isPlaying ? t("pause") : t("play")}
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40"
            style={{ backgroundColor: "#2A2624" }}
          >
            {audioPL.isPlaying ? (
              <Pause className="h-6 w-6 fill-current" />
            ) : (
              <Play className="ml-0.5 h-6 w-6 fill-current" />
            )}
          </button>
          <div className="text-center sm:text-left">
            <p className="text-base" style={{ color: "#1A1A1A", fontWeight: 600, letterSpacing: "0.3px" }}>
              WYJDŹ Z fałszywego ŻYCIA
            </p>
            <p className="text-sm" style={{ color: "rgba(26,26,26,0.6)" }}>
              Polska wersja · MP3
            </p>
          </div>
          <button
            type="button"
            onClick={() => forceDownload(AUDIO_SIDE_PL, "wyjdz-z-falszywego-zycia.mp3")}
            data-testid="polish-download-button"
            aria-label="Download Polska wersja"
            className="ml-auto inline-flex items-center gap-2 text-sm text-white transition-colors duration-200 hover:opacity-90"
            style={{
              backgroundColor: "#2A2624",
              padding: "10px 18px",
              borderRadius: "5px",
              fontWeight: 500,
              letterSpacing: "0.3px",
            }}
          >
            <Download className="h-4 w-4" />
            <span>Pobierz</span>
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
